import { IconDeviceFloppy } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslations } from "next-intl";

type TimePickerProps = {
  currentDate: Date;
  setCurrentDate: (date: Date) => void;
  onSelectDate: (date: Date) => void;
};

export const TimePicker: React.FC<TimePickerProps> = ({
  currentDate,
  setCurrentDate,
  onSelectDate,
}) => {
  const t = useTranslations("datePicker");
  // Use getUTCHours and getUTCMinutes to work strictly with UTC time
  const [hours, setHours] = useState(
    currentDate.getUTCHours().toString().padStart(2, "0"),
  );
  const [minutes, setMinutes] = useState(
    currentDate.getUTCMinutes().toString().padStart(2, "0"),
  );

  // Handle hours change with better user experience
  const handleHoursChange = (value: string) => {
    // Allow empty input for easier deletion
    if (value === "") {
      setHours("");
      return;
    }

    // Only allow numeric input
    if (!/^\d*$/.test(value)) {
      return;
    }

    const numValue = parseInt(value);

    // Handle single digit
    if (value?.length === 1) {
      setHours(value);
      const newDate = new Date(currentDate.getTime());
      newDate.setUTCHours(numValue);
      setCurrentDate(newDate);
      return;
    }

    // Validate range (0-23) for complete value
    if (numValue >= 0 && numValue <= 23) {
      setHours(value);
      const newDate = new Date(currentDate.getTime());
      newDate.setUTCHours(numValue);
      setCurrentDate(newDate);
    }
  };

  // Handle minutes change with better user experience
  const handleMinutesChange = (value: string) => {
    // Allow empty input for easier deletion
    if (value === "") {
      setMinutes("");
      return;
    }

    // Only allow numeric input
    if (!/^\d*$/.test(value)) {
      return;
    }

    const numValue = parseInt(value);

    // Handle single digit
    if (value?.length === 1) {
      setMinutes(value);
      const newDate = new Date(currentDate.getTime());
      newDate.setUTCMinutes(numValue);
      setCurrentDate(newDate);
      return;
    }

    // Validate range (0-59) for complete value
    if (numValue >= 0 && numValue <= 59) {
      setMinutes(value);
      const newDate = new Date(currentDate.getTime());
      newDate.setUTCMinutes(numValue);
      setCurrentDate(newDate);
    }
  };

  // Handle blur event to ensure proper formatting
  const handleBlur = (type: "hours" | "minutes") => {
    if (type === "hours") {
      // Ensure hours is properly formatted when input loses focus
      if (hours === "") {
        setHours("00");
        const newDate = new Date(currentDate.getTime());
        newDate.setUTCHours(0);
        setCurrentDate(newDate);
      } else {
        setHours(hours.padStart(2, "0"));
      }
    } else {
      // Ensure minutes is properly formatted when input loses focus
      if (minutes === "") {
        setMinutes("00");
        const newDate = new Date(currentDate.getTime());
        newDate.setUTCMinutes(0);
        setCurrentDate(newDate);
      } else {
        setMinutes(minutes.padStart(2, "0"));
      }
    }
  };

  return (
    <div className="flex items-center pt-2 mt-2 border-t outline-tertiary ">
      <div className="w-full flex justify-between items-center ml-2">
        {/* The commit action for date+time fields (a day click only selects
            the day now) — a clear filled button, not a bare grey icon. */}
        <button
          type="button"
          aria-label={t("apply")}
          title={t("apply")}
          onClick={() => {
            onSelectDate(currentDate);
          }}
          className="mr-2 flex shrink-0 items-center gap-1.5 rounded-md cta-primary font-light-primary hover:cta-hover transition-colors px-2.5 py-1.5 body-3-medium"
        >
          <IconDeviceFloppy size={16} />
          {t("apply")}
        </button>
        <div className="flex flex-row w-full justify-center items-center">
          <input
            type="text"
            value={hours}
            onChange={(e) => handleHoursChange(e.target.value)}
            onBlur={() => handleBlur("hours")}
            className="w-11 px-3 surface-primary py-1.5 body-3-regular focus:focus-primary text-center border rounded-md outline-quaternary"
            maxLength={2}
            placeholder="00"
          />
          <span className="mx-1 body-3-regular">:</span>
          <input
            type="text"
            value={minutes}
            onChange={(e) => handleMinutesChange(e.target.value)}
            onBlur={() => handleBlur("minutes")}
            className="w-11 px-3 surface-primary py-1.5 body-3-regular focus:focus-primary text-center border outline-tertiary focus:none rounded-md"
            maxLength={2}
            placeholder="00"
          />
        </div>
      </div>
    </div>
  );
};
